import React, { useEffect, useState } from 'react';
import DocumentUpload from '../../common/DocumentUpload';
import { FormService } from '../../../services/patientmr/forms.service';
import type { FormDocumentDto, FormRecordDto } from '../../../dtos/patientmr/forms.dto';

interface CustomFormProps {
  isReadOnly?: boolean;
  initialData?: Record<string, unknown>;
  onDataChange?: (data: Record<string, unknown>) => void;
  patientId?: string;
  formRecord?: FormRecordDto;
  onFormRecordChange?: (record: FormRecordDto) => void;
}

const inputClass = 'w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500 text-sm';

const CustomForm: React.FC<CustomFormProps> = ({
  isReadOnly = false,
  initialData = {},
  onDataChange,
  patientId,
  formRecord,
  onFormRecordChange,
}) => {
  const [name, setName] = useState(String(initialData.name ?? ''));
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(String(initialData.name ?? ''));
  }, [initialData.name]);

  useEffect(() => {
    onDataChange?.({ ...initialData, name });
  }, [name, initialData, onDataChange]);

  const canEdit = !isReadOnly && (!formRecord || formRecord.status === 'DRAFT');

  const handleFilesSelected = (files: File[]) => {
    if (!canEdit) return;
    setError(null);
    setStagedFiles(files.slice(0, 1));
  };

  const handleUpload = async () => {
    if (!patientId || !formRecord || stagedFiles.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const updated = await FormService.addDocument(patientId, formRecord.formId, stagedFiles[0]);
      setStagedFiles([]);
      onFormRecordChange?.(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: FormDocumentDto) => {
    if (!patientId || !formRecord) return;
    try {
      const result = await FormService.getDocumentUrl(patientId, formRecord.formId, doc.id);
      window.open(result.url, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed. Please try again.');
    }
  };

  const handleDelete = async (doc: FormDocumentDto) => {
    if (!patientId || !formRecord || !canEdit) return;
    setDeletingId(doc.id);
    setError(null);
    try {
      const updated = await FormService.deleteDocument(patientId, formRecord.formId, doc.id);
      onFormRecordChange?.(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-5 p-4">
      <div>
        <label htmlFor="custom-form-name" className="block text-sm font-medium text-gray-700 mb-1">
          Form Name <span className="text-red-500">*</span>
        </label>
        <input
          id="custom-form-name"
          type="text"
          value={name}
          onChange={event => setName(event.target.value)}
          readOnly={!canEdit}
          required
          className={inputClass}
        />
      </div>

      {error && <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}

      {formRecord && (
        <>
          <DocumentUpload
            documents={(formRecord.documents ?? []).map(document => ({ id: document.id, name: document.name, size: document.size, uploadedAt: document.uploadedAt }))}
            stagedFiles={stagedFiles}
            canUpload={canEdit}
            onFilesSelected={handleFilesSelected}
            onStagedRemove={index => setStagedFiles(files => files.filter((_, fileIndex) => fileIndex !== index))}
            onDownload={handleDownload}
            onDelete={canEdit ? handleDelete : undefined}
            uploading={uploading}
            deletingId={deletingId}
            collapsible
            defaultOpen
          />
          {stagedFiles.length > 0 && canEdit && (
            <div className="flex justify-end">
              <button type="button" onClick={handleUpload} disabled={uploading} className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md text-sm font-medium disabled:opacity-50">
                {uploading ? 'Uploading...' : 'Upload File'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CustomForm;